// ManiDine Restaurant Web — src/pages/reports/SalesReports.jsx — Updated: 2026-03-31 13:00 (America/Edmonton)
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import useRestaurantContext from "../../hooks/useRestaurantContext";
import {
  ReportCard,
  ReportChart,
  ReportFilterBar,
  ReportTable,
} from "../../components/reports";

const defaultDateRange = { preset: "7d", start: "", end: "" };

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(Number(value || 0));

// Convert Firestore timestamp / date / string -> JS Date
const getOrderDate = (createdAt) => {
  if (!createdAt) return null;
  if (typeof createdAt.toDate === "function") return createdAt.toDate();
  if (createdAt instanceof Date) return createdAt;
  const parsed = new Date(createdAt);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getOrderTimelineDate = (order) =>
  getOrderDate(
    order?.createdAt || order?.placedAt || order?.orderTime || order?.updatedAt
  );

const normalizeStatus = (status) => {
  if (!status) return "";
  return String(status).toLowerCase();
};

const isSalesEligibleStatus = (status) => {
  const s = normalizeStatus(status);
  if (!s) return true;
  if (
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("refun") ||
    s.includes("void") ||
    s.includes("declin")
  ) {
    return false;
  }
  return (
    s.includes("complete") ||
    s.includes("deliver") ||
    s.includes("finish") ||
    s.includes("paid") ||
    s.includes("accept") ||
    s.includes("on_the_way")
  );
};

// Turn preset/custom into start/end Date
const resolveDateRange = (dateRange) => {
  const now = new Date();
  let start = null;
  let end = null;

  if (dateRange.preset === "today") {
    start = new Date();
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
  } else if (dateRange.preset === "7d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "30d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "custom") {
    if (dateRange.start) {
      start = new Date(dateRange.start);
      start.setHours(0, 0, 0, 0);
    }
    if (dateRange.end) {
      end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
    }
  }

  return { start, end };
};

const parseNumber = (value, fallback = 0) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

const getOrderTax = (order) => {
  if (typeof order?.tax === "number") return order.tax;
  if (typeof order?.taxAmount === "number") return order.taxAmount;
  return 0;
};

const getOrderTip = (order) => {
  if (typeof order?.tip === "number") return order.tip;
  if (typeof order?.tipAmount === "number") return order.tipAmount;
  return 0;
};

const getOrderDeliveryFee = (order) => {
  if (typeof order?.deliveryFee === "number") return order.deliveryFee;
  if (typeof order?.deliveryCharge === "number") return order.deliveryCharge;
  if (typeof order?.shippingFee === "number") return order.shippingFee;
  return 0;
};

const getItemsFoodSubtotal = (order) =>
  (order?.items || []).reduce((sum, item) => {
    const price = parseNumber(item?.price);
    const quantity = parseNumber(item?.quantity ?? 1, 1);
    return sum + price * quantity;
  }, 0);

const getRestaurantFoodSubtotal = (order) => {
  if (typeof order?.subtotal === "number") return order.subtotal;
  if (typeof order?.itemsSubtotal === "number") return order.itemsSubtotal;
  if (typeof order?.foodSubtotal === "number") return order.foodSubtotal;

  const itemsSubtotal = getItemsFoodSubtotal(order);
  if (itemsSubtotal > 0) return itemsSubtotal;

  if (typeof order?.totalAmount === "number" || typeof order?.total === "number") {
    const total = parseNumber(order?.totalAmount ?? order?.total);
    const tax = getOrderTax(order);
    const deliveryFee = getOrderDeliveryFee(order);
    const tip = getOrderTip(order);
    return Math.max(total - tax - deliveryFee - tip, 0);
  }

  return 0;
};

const getGroupingKey = (date, grouping) => {
  if (!date) return "Unknown";

  if (grouping === "week") {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const day = start.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(start.getDate() + diff);

    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const startTxt = start.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    const endTxt = end.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${startTxt} – ${endTxt}`;
  }

  if (grouping === "month") {
    return date.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const SalesReports = () => {
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [grouping, setGrouping] = useState("day");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        if (!restaurantId) {
          console.warn("[SalesReports] No restaurantId yet, skipping fetch");
          setOrders([]);
          setLoading(false);
          return;
        }

        const ordersRef = collection(db, "orders");
        const ordersQuery = query(
          ordersRef,
          where("restaurantId", "==", restaurantId)
        );
        const snap = await getDocs(ordersQuery);
        const rows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        console.log(
          "[SalesReports] Loaded orders for restaurant",
          restaurantId,
          "count:",
          rows.length
        );
        setOrders(rows);
      } catch (err) {
        console.error("[SalesReports] Load orders failed:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantLoading) {
      setLoading(true);
      return;
    }

    fetchOrders();
  }, [restaurantId, restaurantLoading]);

  const filteredOrders = useMemo(() => {
    if (!orders.length) return [];
    const { start, end } = resolveDateRange(dateRange);
    const result = orders.filter((order) => {
      if (!isSalesEligibleStatus(order?.status)) return false;
      const d = getOrderTimelineDate(order);
      if (!d) return false;
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
    console.log(
      "[SalesReports] Filtered eligible orders for range",
      dateRange,
      "=>",
      result.length
    );
    return result;
  }, [orders, dateRange]);

  const derivedOrders = useMemo(() => {
    return filteredOrders.map((order) => {
      const foodSubtotal = parseNumber(getRestaurantFoodSubtotal(order));
      const tax = parseNumber(getOrderTax(order));
      const orderDate = getOrderTimelineDate(order);

      return {
        ...order,
        __foodSubtotal: foodSubtotal,
        __tax: tax,
        __restaurantSalesTotal: foodSubtotal + tax,
        __date: orderDate,
      };
    });
  }, [filteredOrders]);

  const kpis = useMemo(() => {
    const totalOrders = derivedOrders.length;
    const foodSales = derivedOrders.reduce(
      (sum, order) => sum + order.__foodSubtotal,
      0
    );
    const taxes = derivedOrders.reduce((sum, order) => sum + order.__tax, 0);
    const restaurantSalesTotal = derivedOrders.reduce(
      (sum, order) => sum + order.__restaurantSalesTotal,
      0
    );
    const averageFoodOrderValue = totalOrders > 0 ? foodSales / totalOrders : 0;

    return {
      totalOrders,
      foodSales,
      taxes,
      restaurantSalesTotal,
      averageFoodOrderValue,
    };
  }, [derivedOrders]);

  const salesOverTime = useMemo(() => {
    const map = new Map();

    derivedOrders.forEach((order) => {
      const key = getGroupingKey(order.__date, grouping);
      const prev = map.get(key) || 0;
      map.set(key, prev + order.__restaurantSalesTotal);
    });

    return [...map.entries()].map(([date, total]) => ({ date, total }));
  }, [derivedOrders, grouping]);

  const categoryTable = useMemo(() => {
    const map = new Map();
    derivedOrders.forEach((order) => {
      (order.items || []).forEach((item) => {
        const key = item.categoryName || "Uncategorized";
        const prev = map.get(key) || { sales: 0, orders: 0 };
        map.set(key, {
          sales:
            prev.sales +
            parseNumber(item.price) * parseNumber(item.quantity || 1, 1),
          orders: prev.orders + 1,
        });
      });
    });

    const totalSales =
      [...map.values()].reduce((sum, value) => sum + value.sales, 0) || 1;

    return [...map.entries()]
      .map(([name, value]) => ({
        id: name,
        name,
        sales: value.sales,
        orders: value.orders,
        share: `${((value.sales / totalSales) * 100).toFixed(1)}%`,
      }))
      .sort((a, b) => b.sales - a.sales);
  }, [derivedOrders]);

  const itemTable = useMemo(() => {
    const map = new Map();
    derivedOrders.forEach((order) => {
      (order.items || []).forEach((item) => {
        const key = item.itemId || item.itemName;
        const quantity = parseNumber(item.quantity || 1, 1);
        const price = parseNumber(item.price);
        const prev =
          map.get(key) || {
            id: key,
            name: item.itemName || "Item",
            orders: 0,
            qty: 0,
            sales: 0,
          };

        map.set(key, {
          ...prev,
          orders: prev.orders + 1,
          qty: prev.qty + quantity,
          sales: prev.sales + price * quantity,
        });
      });
    });

    return [...map.values()].sort((a, b) => b.sales - a.sales);
  }, [derivedOrders]);

  if (restaurantLoading) {
    return (
      <div className="reports-section">
        <p className="muted">Loading restaurant…</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="reports-section">
        <p className="muted">No restaurant found. Please sign in again.</p>
      </div>
    );
  }

  return (
    <div className="reports-section">
      <ReportFilterBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        grouping={grouping}
        onGroupingChange={setGrouping}
      />

      <section className="reports-kpi-grid">
        <ReportCard title="Total Orders" value={kpis.totalOrders} />
        <ReportCard title="Food Sales" value={formatCurrency(kpis.foodSales)} />
        <ReportCard
          title="Tax on Food Sales"
          value={formatCurrency(kpis.taxes)}
        />
        <ReportCard
          title="Restaurant Total"
          value={formatCurrency(kpis.restaurantSalesTotal)}
          subtitle="Food subtotal + tax only"
        />
        <ReportCard
          title="Average Food Order"
          value={formatCurrency(kpis.averageFoodOrderValue)}
        />
      </section>

      <section
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 14,
          background: "rgba(15, 23, 42, 0.04)",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        This page now reports restaurant sales only:
        <strong> food subtotal + tax</strong>. Delivery fees, tips, customer
        paid totals, monthly membership fees, the $1.99 ManiDine service fee per
        order, and Stripe fees are excluded from the numbers shown here.
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Restaurant sales over time</h3>
          <ReportChart type="line" data={salesOverTime} xKey="date" yKey="total" />
        </div>
        <div className="reports-column">
          <h3 className="reports-section-title">
            Restaurant sales by {grouping}
          </h3>
          <ReportChart
            type="bar"
            data={salesOverTime}
            xKey="date"
            series={[{ dataKey: "total", name: "Restaurant Total" }]}
          />
        </div>
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Category-Level Food Sales</h3>
          <ReportTable
            columns={[
              { title: "Category", dataIndex: "name", key: "name" },
              {
                title: "Sales",
                dataIndex: "sales",
                key: "sales",
                render: (value) => formatCurrency(value),
              },
              { title: "Orders", dataIndex: "orders", key: "orders" },
              { title: "Share", dataIndex: "share", key: "share" },
            ]}
            data={categoryTable}
          />
          <div className="reports-chart-inline">
            <ReportChart type="pie" data={categoryTable} xKey="name" yKey="sales" />
          </div>
        </div>

        <div className="reports-column">
          <h3 className="reports-section-title">Item-Level Food Sales</h3>
          <ReportTable
            columns={[
              { title: "Item", dataIndex: "name", key: "name" },
              { title: "Orders", dataIndex: "orders", key: "orders" },
              { title: "Quantity", dataIndex: "qty", key: "qty" },
              {
                title: "Sales",
                dataIndex: "sales",
                key: "sales",
                render: (value) => formatCurrency(value),
              },
            ]}
            data={itemTable}
          />
        </div>
      </section>

      {loading && <div className="reports-loading">Loading data…</div>}
    </div>
  );
};

export default SalesReports;
